import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  Stack,
  Alert,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Divider,
  InputAdornment,
  Chip,
  Grid,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { authenticatedFetch } from "../../utils/api";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

interface Company {
  id: number;
  name: string;
}

interface UnbilledDelivery {
  id: number;
  created_at: string;
  calculated_total_weight: number;
  calculated_total_rolls: number;
  specs_display: string;
}

interface OrgSettings {
  cgst_percentage: number;
  sgst_percentage: number;
}

const invoiceItemSchema = z.object({
  particulars: z.string().min(1, "Particulars is required"),
  rate: z
    .union([z.number(), z.string()])
    .pipe(z.coerce.number({ required_error: "Rate is required" }))
    .refine((val) => val > 0, "Rate must be positive"),
  weight: z
    .union([z.number(), z.string()])
    .pipe(z.coerce.number({ required_error: "Weight is required" }))
    .refine((val) => val > 0, "Weight must be positive"),
});

const invoiceSchema = z.object({
  company_id: z.number({ required_error: "Company is required" }),
  invoice_date: z.string().min(1, "Invoice date is required"),
  cgst_percentage: z
    .union([z.number(), z.string()])
    .pipe(z.coerce.number())
    .refine((val) => val >= 0 && val <= 100, "Must be between 0 and 100"),
  sgst_percentage: z
    .union([z.number(), z.string()])
    .pipe(z.coerce.number())
    .refine((val) => val >= 0 && val <= 100, "Must be between 0 and 100"),
  invoice_items_attributes: z
    .array(invoiceItemSchema)
    .min(1, "At least one rate item is required"),
});

type InvoiceFormData = z.infer<typeof invoiceSchema>;

export default function CreateInvoice() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [error, setError] = useState<string>("");
  const [companies, setCompanies] = useState<Company[]>([]);
  const [unbilledDeliveries, setUnbilledDeliveries] = useState<UnbilledDelivery[]>([]);
  const [selectedDeliveryIds, setSelectedDeliveryIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingDeliveries, setLoadingDeliveries] = useState(false);
  const [orgSettings, setOrgSettings] = useState<OrgSettings | null>(null);

  const { control, handleSubmit, watch, setValue } = useForm<InvoiceFormData>({
    resolver: zodResolver(invoiceSchema),
    defaultValues: {
      company_id: undefined,
      invoice_date: new Date().toISOString().split("T")[0],
      cgst_percentage: 2.5,
      sgst_percentage: 2.5,
      invoice_items_attributes: [
        {
          particulars: "",
          rate: undefined as any,
          weight: undefined as any,
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "invoice_items_attributes",
  });

  const selectedCompanyId = watch("company_id");
  const invoiceItems = watch("invoice_items_attributes");
  const cgstPercentage = watch("cgst_percentage");
  const sgstPercentage = watch("sgst_percentage");

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedCompanyId) {
      fetchUnbilledDeliveries(selectedCompanyId);
      setSelectedDeliveryIds([]);
    } else {
      setUnbilledDeliveries([]);
      setSelectedDeliveryIds([]);
    }
  }, [selectedCompanyId]);

  useEffect(() => {
    const totalWeight = getTotalDeliveryWeight();
    if (totalWeight > 0 && fields.length === 1 && !invoiceItems[0]?.weight) {
      setValue("invoice_items_attributes.0.weight", totalWeight);
    }
  }, [selectedDeliveryIds]);

  const fetchData = async () => {
    try {
      const [companiesRes, orgSettingsRes] = await Promise.all([
        authenticatedFetch(`${API_URL}/companies?per_page=1000&minimal=true`),
        authenticatedFetch(`${API_URL}/organization_settings`),
      ]);

      if (companiesRes.status === 401) {
        navigate("/login");
        return;
      }

      if (!companiesRes.ok) {
        throw new Error("Failed to fetch data");
      }

      const companiesData = await companiesRes.json();
      setCompanies(companiesData);

      if (orgSettingsRes.ok) {
        const orgData = await orgSettingsRes.json();
        setOrgSettings(orgData);
        setValue("cgst_percentage", parseFloat(orgData.cgst_percentage) || 2.5);
        setValue("sgst_percentage", parseFloat(orgData.sgst_percentage) || 2.5);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const fetchUnbilledDeliveries = async (companyId: number) => {
    try {
      setLoadingDeliveries(true);
      const response = await authenticatedFetch(
        `${API_URL}/invoices/unbilled_deliveries?company_id=${companyId}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch deliveries");
      }

      const data = await response.json();
      setUnbilledDeliveries(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoadingDeliveries(false);
    }
  };

  const handleDeliveryToggle = (deliveryId: number) => {
    setSelectedDeliveryIds((prev) => {
      if (prev.includes(deliveryId)) {
        return prev.filter((id) => id !== deliveryId);
      } else {
        return [...prev, deliveryId];
      }
    });
  };

  const getTotalDeliveryWeight = () => {
    return unbilledDeliveries
      .filter((d) => selectedDeliveryIds.includes(d.id))
      .reduce((sum, d) => sum + Number(d.calculated_total_weight), 0);
  };

  const getItemsWeight = () => {
    return invoiceItems?.reduce((sum, item) => sum + (Number(item?.weight) || 0), 0) || 0;
  };

  const calculateSubtotal = () => {
    return invoiceItems?.reduce((sum, item) => {
      const rate = Number(item?.rate) || 0;
      const weight = Number(item?.weight) || 0;
      return sum + rate * weight;
    }, 0) || 0;
  };

  const calculateCGST = () => {
    return (calculateSubtotal() * (Number(cgstPercentage) || 0)) / 100;
  };

  const calculateSGST = () => {
    return (calculateSubtotal() * (Number(sgstPercentage) || 0)) / 100;
  };

  const calculateRoundOff = () => {
    const rawTotal = calculateSubtotal() + calculateCGST() + calculateSGST();
    const roundedTotal = Math.round(rawTotal);
    return roundedTotal - rawTotal;
  };

  const calculateTotal = () => {
    return Math.round(calculateSubtotal() + calculateCGST() + calculateSGST());
  };

  const isWeightValid = () => {
    const deliveryWeight = getTotalDeliveryWeight();
    const itemsWeight = getItemsWeight();
    return Math.abs(deliveryWeight - itemsWeight) < 0.01;
  };

  const onSubmit = async (data: InvoiceFormData) => {
    if (selectedDeliveryIds.length === 0) {
      setError("Please select at least one delivery");
      return;
    }

    if (!isWeightValid()) {
      setError(
        `Total weight in rate items (${getItemsWeight().toFixed(2)} kg) must match delivery weight (${getTotalDeliveryWeight().toFixed(2)} kg)`
      );
      return;
    }

    try {
      const totalWeight = getTotalDeliveryWeight();
      const subtotal = calculateSubtotal();
      const cgstAmount = calculateCGST();
      const sgstAmount = calculateSGST();
      const roundOff = calculateRoundOff();
      const totalAmount = calculateTotal();

      const response = await authenticatedFetch(`${API_URL}/invoices`, {
        method: "POST",
        body: JSON.stringify({
          invoice: {
            company_id: data.company_id,
            invoice_date: data.invoice_date,
            total_weight: totalWeight,
            subtotal: subtotal,
            cgst_percentage: data.cgst_percentage,
            sgst_percentage: data.sgst_percentage,
            cgst_amount: cgstAmount,
            sgst_amount: sgstAmount,
            round_off: roundOff,
            total_amount: totalAmount,
            invoice_items_attributes: data.invoice_items_attributes.map((item) => ({
              particulars: item.particulars,
              rate: item.rate,
              weight: item.weight,
              amount: Number(item.rate) * Number(item.weight),
            })),
            invoice_deliveries_attributes: selectedDeliveryIds.map((id) => ({
              delivery_id: id,
            })),
          },
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          typeof errorData === "object"
            ? Object.entries(errorData)
                .map(([key, value]) => `${key}: ${value}`)
                .join(", ")
            : JSON.stringify(errorData)
        );
      }

      const invoice = await response.json();
      navigate(`/invoices/${invoice.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(amount);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Mobile Invoice Item Card
  const renderMobileInvoiceItem = (field: any, index: number) => {
    const rate = Number(invoiceItems[index]?.rate) || 0;
    const weight = Number(invoiceItems[index]?.weight) || 0;
    const amount = rate * weight;

    return (
      <Card key={field.id} variant="outlined" sx={{ mb: 2 }}>
        <CardContent sx={{ pb: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="subtitle2" color="primary">
              Item {index + 1}
            </Typography>
            <IconButton
              color="error"
              onClick={() => remove(index)}
              disabled={fields.length === 1}
              size="small"
            >
              <DeleteIcon />
            </IconButton>
          </Box>

          <Controller
            name={`invoice_items_attributes.${index}.particulars`}
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Particulars"
                placeholder="e.g., Pallava 40s VL"
                size="small"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
                multiline
                maxRows={2}
                sx={{ mb: 2 }}
              />
            )}
          />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Controller
                name={`invoice_items_attributes.${index}.rate`}
                control={control}
                render={({
                  field: { onChange, value, ...fieldProps },
                  fieldState,
                }) => (
                  <TextField
                    {...fieldProps}
                    value={value || ""}
                    onChange={(e) =>
                      onChange(e.target.value ? Number(e.target.value) : undefined)
                    }
                    label="Rate"
                    type="number"
                    size="small"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    fullWidth
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name={`invoice_items_attributes.${index}.weight`}
                control={control}
                render={({
                  field: { onChange, value, ...fieldProps },
                  fieldState,
                }) => (
                  <TextField
                    {...fieldProps}
                    value={value || ""}
                    onChange={(e) =>
                      onChange(e.target.value ? Number(e.target.value) : undefined)
                    }
                    label="Weight (kg)"
                    type="number"
                    size="small"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    fullWidth
                  />
                )}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, pt: 1, borderTop: "1px dashed", borderColor: "divider" }}>
            <Typography variant="body2" color="text.secondary">
              Amount: <strong>{formatCurrency(amount)}</strong>
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Mobile Delivery Card
  const renderMobileDeliveryCard = (delivery: UnbilledDelivery) => {
    const isSelected = selectedDeliveryIds.includes(delivery.id);
    return (
      <Card
        key={delivery.id}
        variant="outlined"
        onClick={() => handleDeliveryToggle(delivery.id)}
        sx={{
          mb: 1.5,
          cursor: "pointer",
          borderColor: isSelected ? "primary.main" : "divider",
          bgcolor: isSelected ? "primary.50" : "background.paper",
          transition: "all 0.2s",
        }}
      >
        <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Checkbox
              checked={isSelected}
              onChange={() => handleDeliveryToggle(delivery.id)}
              sx={{ p: 0 }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Chip label={`DC #${delivery.id}`} size="small" color={isSelected ? "primary" : "default"} />
                <Typography variant="caption" color="text.secondary">
                  {new Date(delivery.created_at).toLocaleDateString()}
                </Typography>
              </Box>
              <Grid container spacing={1} sx={{ mt: 0.5 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Rolls</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {delivery.calculated_total_rolls}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Weight</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {Number(delivery.calculated_total_weight).toFixed(2)} kg
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Container maxWidth="md" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      <Card sx={{ mt: { xs: 2, sm: 4 } }}>
        <CardContent sx={{ px: { xs: 2, sm: 3 } }}>
          <Box
            sx={{
              mb: 3,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "stretch", sm: "center" },
              gap: 2,
            }}
          >
            <Typography variant={isMobile ? "h6" : "h5"} component="h2">
              Create Invoice
            </Typography>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/invoices")}
              variant="outlined"
              fullWidth={isMobile}
            >
              Back
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            {/* Company Selection */}
            <Controller
              name="company_id"
              control={control}
              render={({ field, fieldState }) => (
                <FormControl fullWidth margin="normal" error={!!fieldState.error}>
                  <InputLabel>Company</InputLabel>
                  <Select
                    {...field}
                    label="Company"
                    value={field.value || ""}
                    MenuProps={{
                      PaperProps: {
                        style: { maxHeight: 300 },
                      },
                    }}
                  >
                    {companies.map((company) => (
                      <MenuItem key={company.id} value={company.id}>
                        {company.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {fieldState.error && (
                    <FormHelperText>{fieldState.error.message}</FormHelperText>
                  )}
                </FormControl>
              )}
            />

            {/* Invoice Date */}
            <Controller
              name="invoice_date"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Invoice Date"
                  type="date"
                  fullWidth
                  margin="normal"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />

            {/* Deliveries Selection */}
            {selectedCompanyId && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Select Deliveries (DC)
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {loadingDeliveries ? (
                  <Box display="flex" justifyContent="center" py={3}>
                    <CircularProgress size={30} />
                  </Box>
                ) : unbilledDeliveries.length === 0 ? (
                  <Alert severity="info">
                    No unbilled deliveries found for this company.
                  </Alert>
                ) : isMobile ? (
                  // Mobile: Card-based layout
                  <Box>
                    {unbilledDeliveries.map((delivery) => renderMobileDeliveryCard(delivery))}
                  </Box>
                ) : (
                  // Desktop: Table layout
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">Select</TableCell>
                          <TableCell>DC No</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Specs</TableCell>
                          <TableCell align="right">Rolls</TableCell>
                          <TableCell align="right">Weight (kg)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {unbilledDeliveries.map((delivery) => (
                          <TableRow
                            key={delivery.id}
                            hover
                            onClick={() => handleDeliveryToggle(delivery.id)}
                            sx={{ cursor: "pointer" }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedDeliveryIds.includes(delivery.id)}
                                onChange={() => handleDeliveryToggle(delivery.id)}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip label={delivery.id} size="small" color="primary" />
                            </TableCell>
                            <TableCell>
                              {new Date(delivery.created_at).toLocaleDateString()}
                            </TableCell>
                            <TableCell>{delivery.specs_display}</TableCell>
                            <TableCell align="right">
                              {delivery.calculated_total_rolls}
                            </TableCell>
                            <TableCell align="right">
                              {Number(delivery.calculated_total_weight).toFixed(2)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {selectedDeliveryIds.length > 0 && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: "success.50", borderRadius: 1, border: "1px solid", borderColor: "success.200" }}>
                    <Typography variant="body2">
                      <strong>Selected DCs:</strong> {selectedDeliveryIds.join(", ")}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Total Weight:</strong> {getTotalDeliveryWeight().toFixed(2)} kg
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Rate Items */}
            {selectedDeliveryIds.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
                  <Typography variant="h6">
                    Invoice Items
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() =>
                      append({
                        particulars: "",
                        rate: undefined as any,
                        weight: undefined as any,
                      })
                    }
                  >
                    Add Item
                  </Button>
                </Box>
                
                <Alert severity="info" sx={{ mb: 2 }}>
                  Total weight must equal {getTotalDeliveryWeight().toFixed(2)} kg
                </Alert>

                {isMobile ? (
                  // Mobile: Card-based layout
                  <Box>
                    {fields.map((field, index) => renderMobileInvoiceItem(field, index))}
                  </Box>
                ) : (
                  // Desktop: Table layout
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ minWidth: 200 }}>Particulars</TableCell>
                          <TableCell sx={{ minWidth: 100 }}>Rate (₹/kg)</TableCell>
                          <TableCell sx={{ minWidth: 100 }}>Weight (kg)</TableCell>
                          <TableCell align="right" sx={{ minWidth: 100 }}>Amount (₹)</TableCell>
                          <TableCell width={60}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {fields.map((field, index) => {
                          const rate = Number(invoiceItems[index]?.rate) || 0;
                          const weight = Number(invoiceItems[index]?.weight) || 0;
                          const amount = rate * weight;

                          return (
                            <TableRow key={field.id}>
                              <TableCell>
                                <Controller
                                  name={`invoice_items_attributes.${index}.particulars`}
                                  control={control}
                                  render={({ field, fieldState }) => (
                                    <TextField
                                      {...field}
                                      placeholder="e.g., Pallava 40s VL"
                                      size="small"
                                      error={!!fieldState.error}
                                      helperText={fieldState.error?.message}
                                      fullWidth
                                      multiline
                                      maxRows={2}
                                    />
                                  )}
                                />
                              </TableCell>
                              <TableCell>
                                <Controller
                                  name={`invoice_items_attributes.${index}.rate`}
                                  control={control}
                                  render={({
                                    field: { onChange, value, ...fieldProps },
                                    fieldState,
                                  }) => (
                                    <TextField
                                      {...fieldProps}
                                      value={value || ""}
                                      onChange={(e) =>
                                        onChange(
                                          e.target.value ? Number(e.target.value) : undefined
                                        )
                                      }
                                      type="number"
                                      size="small"
                                      error={!!fieldState.error}
                                      helperText={fieldState.error?.message}
                                      fullWidth
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position="start">₹</InputAdornment>
                                        ),
                                      }}
                                    />
                                  )}
                                />
                              </TableCell>
                              <TableCell>
                                <Controller
                                  name={`invoice_items_attributes.${index}.weight`}
                                  control={control}
                                  render={({
                                    field: { onChange, value, ...fieldProps },
                                    fieldState,
                                  }) => (
                                    <TextField
                                      {...fieldProps}
                                      value={value || ""}
                                      onChange={(e) =>
                                        onChange(
                                          e.target.value ? Number(e.target.value) : undefined
                                        )
                                      }
                                      type="number"
                                      size="small"
                                      error={!!fieldState.error}
                                      helperText={fieldState.error?.message}
                                      fullWidth
                                    />
                                  )}
                                />
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(amount)}
                              </TableCell>
                              <TableCell>
                                <IconButton
                                  color="error"
                                  onClick={() => remove(index)}
                                  disabled={fields.length === 1}
                                  size="small"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {!isWeightValid() && getItemsWeight() > 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    Weight mismatch: Items total ({getItemsWeight().toFixed(2)} kg) ≠
                    Delivery total ({getTotalDeliveryWeight().toFixed(2)} kg)
                  </Alert>
                )}
              </Box>
            )}

            {/* Tax Configuration */}
            {selectedDeliveryIds.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Tax Configuration
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Controller
                      name="cgst_percentage"
                      control={control}
                      render={({ field: { onChange, value, ...fieldProps }, fieldState }) => (
                        <TextField
                          {...fieldProps}
                          value={value}
                          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
                          label="CGST %"
                          type="number"
                          size="small"
                          fullWidth
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                          inputProps={{ step: "0.01", min: "0", max: "100" }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Controller
                      name="sgst_percentage"
                      control={control}
                      render={({ field: { onChange, value, ...fieldProps }, fieldState }) => (
                        <TextField
                          {...fieldProps}
                          value={value}
                          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
                          label="SGST %"
                          type="number"
                          size="small"
                          fullWidth
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                          inputProps={{ step: "0.01", min: "0", max: "100" }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Summary */}
            {selectedDeliveryIds.length > 0 && invoiceItems.some((i) => i.rate && i.weight) && (
              <Box sx={{ mt: 3, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
                <Typography variant="h6" gutterBottom>
                  Invoice Summary
                </Typography>
                <Stack spacing={1}>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2">Subtotal:</Typography>
                    <Typography variant="body2">{formatCurrency(calculateSubtotal())}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2">CGST ({cgstPercentage}%):</Typography>
                    <Typography variant="body2">{formatCurrency(calculateCGST())}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2">SGST ({sgstPercentage}%):</Typography>
                    <Typography variant="body2">{formatCurrency(calculateSGST())}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2">Round Off:</Typography>
                    <Typography variant="body2">{formatCurrency(calculateRoundOff())}</Typography>
                  </Box>
                  <Divider />
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="subtitle1" fontWeight="bold">Total:</Typography>
                    <Typography variant="subtitle1" fontWeight="bold" color="primary">
                      {formatCurrency(calculateTotal())}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            )}

            <Stack 
              direction={{ xs: "column", sm: "row" }} 
              spacing={2} 
              sx={{ mt: 3 }}
            >
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={selectedDeliveryIds.length === 0 || !isWeightValid()}
              >
                Create Invoice
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate("/invoices")}
                fullWidth
              >
                Cancel
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
}
